/* eslint-disable no-fallthrough */
/* eslint-disable react-hooks/exhaustive-deps */
import propTypes from 'prop-types';
import { useCallback, useDebugValue, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import moment from 'moment';

import axios from '../configuraciones/axios';
import general from '../configuraciones/general';
import endpoints, {
  INVERNADEROS,
  NAVES,
  SITIOS,
} from '../configuraciones/endpoints';
import { VALIDAR_SELECCION_REPORTE } from '../configuraciones/mensajes';

import { setRegistros } from '../ducks/listados';
import { getWeeksFormatted, getWeeks } from '../utilidades/functions';

const jerarquia = {
  sitios: {
    hijos: ['invernaderos', 'naves'],
    relacion: (padre, hijo) => padre.id === hijo.sitioID,
  },
  invernaderos: {
    hijos: ['naves'],
    relacion: (padre, hijo) => padre.id === hijo.invernaderoID,
  },
  naves: {
    hijos: ['tablas', 'secciones', 'trampas'],
    relacion: (padre, hijo) => padre.id === hijo.naveID,
  },
  tablas: {
    hijos: ['plantas', 'surcos'],
    relacion: (padre, hijo) => padre.id === hijo.tablaID,
  },
};

/**
 * Función que se encarga de manejar las dependencias entre los objetos
 * @param {string} nivel
 * @param {Object} nuevoEstado
 * @param {Array<Object>} data
 * @returns Nuevo estado
 */
const limpiarDescendencia = (nivel, nuevoEstado, data) => {
  const config = jerarquia[nivel];
  if (!config) return nuevoEstado;

  for (const hijoKey of config.hijos) {
    const hijoData = data[hijoKey];
    const padreKey = nivel;
    // eslint-disable-next-line no-loop-func
    const padresSeleccionados = data[padreKey]?.filter((p) =>
      nuevoEstado[padreKey]?.includes(p.id),
    );
    if (!padresSeleccionados) continue;

    if (nuevoEstado[hijoKey]) {
      nuevoEstado[hijoKey] = nuevoEstado[hijoKey]?.filter((id) => {
        const hijo = hijoData.find((x) => x.id === id);
        return (
          hijo &&
          padresSeleccionados.some((p) => jerarquia[padreKey].relacion(p, hijo))
        );
      });

      nuevoEstado = limpiarDescendencia(hijoKey, nuevoEstado, data);
    }
  }

  return nuevoEstado;
};

/**
 * Hook personalizado para gestionar componentes de reportería.
 *
 * @param {Record<string, string>} initEndpoints - Endpoints iniciales para obtener los datos base de los filtros.
 * @param {string} reporteEndpoint - Endpoint principal para consultar los datos del reporte.
 * @param {{ formatoFecha: string; incluirInvernaderos: boolean }} opciones - Opciones de configuración para el comportamiento del hook.
 *
 * @returns {{
 *   data: Record<string, string[]>;         // Datos cargados por cada filtro (ej. listas desplegables).
 *   formData: Record<string, number[]>;     // Datos seleccionados en los filtros por el usuario.
 *   onChange: (campo: string, valores: number[]) => void; // Maneja cambios en los filtros.
 *   buscar: () => void;                     // Ejecuta la búsqueda con los filtros actuales.
 *   onDelete: (campo: string) => void;      // Elimina un filtro específico.
 * }} Objetos y funciones expuestos por el hook.
 */
export const useFetchReporte = (
  initEndpoints,
  reporteEndpoint,
  opciones = {},
) => {
  const { formatoFecha = 'W-GGGG', incluirInvernaderos = true } = opciones;

  const dispatch = useDispatch();
  const location = useLocation();
  const isMounted = useRef(false);
  const paginaActual = useRef(1);
  const abortControllerRef = useRef(new AbortController());

  const [data, setData] = useState({
    sitios: [],
    invernaderos: [],
    naves: [],
    semanas: [],
    semanasSinFormato: [],
  });
  const [formData, setFormData] = useState({
    sitios: [],
    naves: [],
    semana: moment().isoWeek(),
    ...Object.keys(initEndpoints).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {}),
  });

  const consultarDatosIniciales = async () => {
    try {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const staticEndpoints = [
        axios.get(endpoints.base.busqueda(SITIOS), { signal }),
        incluirInvernaderos
          ? axios.get(endpoints.base.busqueda(INVERNADEROS), { signal })
          : new Promise((resolve) => resolve([])),
        axios.get(endpoints.base.busqueda(NAVES), { signal }),
      ];

      // Separamos los endpoints que son strings y funciones
      const dynamicEntries = Object.entries(initEndpoints);
      const stringEndpoints = dynamicEntries.filter(
        ([, value]) => typeof value === 'string',
      );
      const functionEndpoints = dynamicEntries.filter(
        ([, value]) => typeof value === 'function',
      );

      // Peticiones solo a los endpoints string
      const dynamicPromises = stringEndpoints.map(([, url]) =>
        axios.get(url, { signal }),
      );
      const responses = await Promise.all([
        ...staticEndpoints,
        ...dynamicPromises,
      ]);

      const [sitios, invernaderos, naves, ...rest] = responses;

      // Normalizamos los datos
      const mappedRest = rest.map((data) =>
        data.map(({ id, nombre, ...rest }) => ({ id, nombre, ...rest })),
      );

      // Construimos el resultado final
      const processed = {
        ...Object.fromEntries(
          stringEndpoints.map(([key], i) => [key, mappedRest[i]]),
        ),
        ...Object.fromEntries(
          functionEndpoints.map(([key, fn]) => [key, fn(responses)]),
        ),
      };

      setData({
        sitios,
        invernaderos,
        naves,
        semanas: getWeeksFormatted(),
        semanasSinFormato: getWeeks(),
        ...processed,
      });
    } catch (error) {
      return error;
    }
  };

  const onChange = ({ target: { name, value } }) => {
    if (!Array.isArray(value)) {
      setFormData({ ...formData, [name]: value });
      return;
    }

    const ultimoValor = value.at(-1);
    const valorFinal = ultimoValor === 0 ? [0] : value.filter(Boolean);
    let nuevoEstado = { ...formData, [name]: valorFinal };

    nuevoEstado = limpiarDescendencia(name, nuevoEstado, data);

    setFormData(nuevoEstado);
    paginaActual.current = 1;
  };

  const onDelete = (id, name) => {
    let nuevoEstado = {
      ...formData,
      [name]: formData[name].filter((x) => x !== id),
    };

    nuevoEstado = limpiarDescendencia(name, nuevoEstado, data);

    setFormData(nuevoEstado);
    paginaActual.current = 1;
  };

  const validarSeleccion = useCallback(() => {
    const esInvalido = Object.values(formData).some(
      (value) => value.length === 0 || value === undefined || value === null
    );

    if (esInvalido) toast.error(VALIDAR_SELECCION_REPORTE);

    return esInvalido;
  }, [formData]);

  const buscar = async () => {
    try {
      if (validarSeleccion()) return;

      const { sitios, invernaderos, naves, incluirValoresCero, semana, fecha, ...rest } = formData;
      const params = Object.keys(initEndpoints).reduce((acc, key) => {
        if (rest[key].length > 0) {
          acc[key] = rest[key];
        }
        return acc;
      }, {});

      const response = await axios.post(reporteEndpoint, {
        ...params,
        sitios,
        invernaderos,
        naves,
        incluirValoresCero,
        fecha: moment(fecha).format(formatoFecha),
        registrosPorPagina: general.ELEMENTOS_POR_PAGINA,
        pagina: paginaActual.current,
      });

      dispatch(setRegistros({ rows: response.rows, count: response.count }));
    } catch {}
  };

  useEffect(() => {
    if (isMounted.current) {
      const query = queryString.parse(location.search);
      if (query.pagina === paginaActual || !query.pagina) return;
      paginaActual.current = query.pagina;
      buscar();
    }
  }, [location]);

  useEffect(() => {
    isMounted.current = true;
    consultarDatosIniciales();

    return () => {
      isMounted.current = false;
      paginaActual.current = 1;
      dispatch(setRegistros({ rows: [], count: 0 }));
      abortControllerRef.current.abort();
    };
  }, []);

  useDebugValue('Fetch Reporte');

  return {
    data,
    formData,
    onChange,
    buscar,
    onDelete,
  };
};

useFetchReporte.propTypes = {
  /** Endpoints de consulta de filtros */
  initEndpoints: propTypes.objectOf(propTypes.string).isRequired,
  /** Endpoint principal para busqueda y exportación */
  reporteEndpoint: propTypes.string.isRequired,
};
